import { HoursSuggestion, MigrationOverride, MigrationPreviewItem, MigrationPreviewRow } from "@swivl-mono/api-helper";
import { filterClasses } from "@swivl-mono/helpers";
import { WeeklyPeriod } from "@swivl-mono/types";
import { Button, ButtonVariant, Input, Spinner } from "@swivl-mono/ui";
import { useCallback, useEffect, useState } from "react";

import { WeeklyGrid } from "../../../Properties/CortexV3/Components/ScheduleEditor/WeeklyGrid";
import { Badge } from "../../DocumentIngest/Badge";
import { quarantineLabel, warningLabel } from "./MigrationItemRow";

/** Flagged rows of one field that share the same legacy value: fixed once, written for every row. */
export interface FlaggedGroup {
  key: string;
  item: MigrationPreviewItem;
  fromValue: string;
  rows: MigrationPreviewRow[];
  /** True when the rows were held back (quarantine); false when they migrated on a guess (warning). */
  heldBack: boolean;
}

/** Every quarantined or warning row, grouped by field and legacy value. Brand colors can't be fixed here. */
export function flaggedGroups(items: MigrationPreviewItem[]): FlaggedGroup[] {
  const groups = new Map<string, FlaggedGroup>();
  for (const item of items) {
    if (!item.inputKind) continue;
    for (const row of item.rows) {
      if (!row.quarantined && row.warning === null) continue;
      const fromValue = (row.fromValue ?? "").trim();
      const key = `${item.specId}::${row.quarantined ? "held" : "guess"}::${fromValue}`;
      const group = groups.get(key) ?? { key, item, fromValue, rows: [], heldBack: row.quarantined };
      group.rows.push(row);
      groups.set(key, group);
    }
  }
  return [...groups.values()];
}

/** The overrides Apply sends: each resolved group's value, for every row in it. */
export function overridesFor(groups: FlaggedGroup[], resolved: Map<string, string>): MigrationOverride[] {
  return groups.flatMap((g) => {
    const value = resolved.get(g.key);
    if (value === undefined) return [];
    return g.rows.map((r) => ({ specId: g.item.specId, scope: r.scope, scopeId: r.scopeId, value }));
  });
}

/** Legacy hours values with no confident parse, for the suggestion request. */
export function hoursValuesToSuggest(groups: FlaggedGroup[]): string[] {
  return [...new Set(groups.filter((g) => g.item.inputKind === "hours" && g.heldBack && g.fromValue).map((g) => g.fromValue))];
}

interface DetailsNeedsInputProps {
  groups: FlaggedGroup[];
  resolved: Map<string, string>;
  suggestions: Map<string, HoursSuggestion>;
  suggestionsLoading: boolean;
  onResolve: (group: FlaggedGroup, value: string) => void;
  onUndo: (group: FlaggedGroup) => void;
}

/**
 * Flagged Details rows the operator can fix right here, before Apply, instead of after the fact on
 * the review ticket. One card per field and legacy value, so a value shared by many locations is
 * fixed once. Anything left unresolved still goes on the review ticket.
 */
export function DetailsNeedsInput({
  groups,
  resolved,
  suggestions,
  suggestionsLoading,
  onResolve,
  onUndo
}: DetailsNeedsInputProps): React.ReactElement | null {
  if (groups.length === 0) return null;
  const resolvedCount = groups.filter((g) => resolved.has(g.key)).length;

  return (
    <section className="mb-8">
      <div className="mb-3 flex items-baseline justify-between gap-4">
        <h3 className="heading-base">Needs your input</h3>
        <span className="body-sm text-grey-600">
          {resolvedCount} of {groups.length} resolved
        </span>
      </div>
      <p className="body-sm mb-4 text-grey-600">
        These couldn&apos;t be migrated with confidence. Fix them here and they&apos;re written when you apply Details. Anything you leave goes on the
        review ticket.
      </p>
      <div className="flex flex-col gap-4">
        {groups.map((group) => (
          <NeedsInputCard
            key={group.key}
            group={group}
            resolvedValue={resolved.get(group.key)}
            suggestion={suggestions.get(group.fromValue)}
            suggestionsLoading={suggestionsLoading}
            onResolve={onResolve}
            onUndo={onUndo}
          />
        ))}
      </div>
    </section>
  );
}

interface NeedsInputCardProps {
  group: FlaggedGroup;
  resolvedValue: string | undefined;
  suggestion: HoursSuggestion | undefined;
  suggestionsLoading: boolean;
  onResolve: (group: FlaggedGroup, value: string) => void;
  onUndo: (group: FlaggedGroup) => void;
}

function NeedsInputCard({ group, resolvedValue, suggestion, suggestionsLoading, onResolve, onUndo }: NeedsInputCardProps): React.ReactElement {
  const [showLocations, setShowLocations] = useState(false);
  const { item, rows } = group;
  const reason = group.heldBack ? quarantineLabel(rows[0]!.quarantineReason) : warningLabel(rows[0]!.warning);
  const scopeLabels = rows.map((r) => r.scopeLabel);

  const handleToggleLocations = useCallback(() => setShowLocations((v) => !v), []);
  const handleUndo = useCallback(() => onUndo(group), [group, onUndo]);

  return (
    <div className={filterClasses("rounded border bg-white p-4", resolvedValue !== undefined ? "border-grey-400" : "border-grey-200")}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="body-base font-semibold text-grey-900">{item.label}</span>
        <button type="button" onClick={handleToggleLocations} className="body-sm text-grey-600 underline">
          {rows.length === 1 ? scopeLabels[0] : `${rows.length} locations`}
        </button>
      </div>
      {showLocations && rows.length > 1 && <p className="body-xs mt-1 text-grey-600">{scopeLabels.join(", ")}</p>}
      <p className="body-sm mt-1 text-grey-600">{reason}</p>
      {group.fromValue && (
        <p className="body-sm mt-2 rounded bg-grey-50 px-3 py-2 font-mono break-words whitespace-pre-wrap text-grey-800">
          {group.fromValue.replace(/\\n/g, "\n")}
        </p>
      )}

      <div className="mt-4">
        {resolvedValue !== undefined ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="body-sm text-grey-900">
              ✓ Written for {rows.length === 1 ? "this location" : `these ${rows.length} locations`} on Apply
            </span>
            <Button variant={ButtonVariant.GHOST} onClick={handleUndo}>
              Undo
            </Button>
          </div>
        ) : item.inputKind === "hours" ? (
          <HoursEditor group={group} suggestion={suggestion} suggestionsLoading={suggestionsLoading} onResolve={onResolve} />
        ) : item.inputKind === "toggle" ? (
          <ToggleEditor group={group} onResolve={onResolve} />
        ) : (
          <TextEditor group={group} onResolve={onResolve} />
        )}
      </div>
    </div>
  );
}

interface EditorProps {
  group: FlaggedGroup;
  onResolve: (group: FlaggedGroup, value: string) => void;
}

function applyLabel(group: FlaggedGroup): string {
  return group.rows.length === 1 ? "Use for this location" : `Use for all ${group.rows.length} locations`;
}

interface HoursEditorProps extends EditorProps {
  suggestion: HoursSuggestion | undefined;
  suggestionsLoading: boolean;
}

/** Hours in the same weekly editor as the Cortex hours page, pre-filled with the best starting point. */
function HoursEditor({ group, suggestion, suggestionsLoading, onResolve }: HoursEditorProps): React.ReactElement {
  // A guess that was written (warning) starts from what was parsed; a held-back row from the AI suggestion.
  const parsed: WeeklyPeriod[] | null = !group.heldBack && group.rows[0]!.willWrite ? JSON.parse(group.rows[0]!.willWrite) : null;
  const initial = parsed ?? suggestion?.periods ?? null;
  const [periods, setPeriods] = useState<WeeklyPeriod[] | null>(initial);
  const fromSuggestion = parsed === null && suggestion?.periods != null;

  // The suggestion can land after the card renders; adopt it until the operator starts editing.
  useEffect(() => {
    if (periods === null && initial !== null) setPeriods(initial);
  }, [periods, initial]);

  const handleUse = useCallback(() => onResolve(group, JSON.stringify(periods ?? [])), [group, periods, onResolve]);
  const label = applyLabel(group);

  if (group.heldBack && suggestionsLoading && periods === null) {
    return (
      <div className="body-sm flex items-center gap-2 text-grey-600">
        <Spinner /> Reading these hours…
      </div>
    );
  }

  return (
    <div>
      {fromSuggestion && <Badge className="mb-3 bg-grey-100 text-grey-800">AI suggestion. Check it against the legacy text</Badge>}
      {suggestion?.notHours && (
        <p className="body-sm mb-3 text-grey-600">
          This doesn&apos;t look like this facility&apos;s own weekly hours. Enter them, or leave it for the ticket.
        </p>
      )}
      <WeeklyGrid periods={periods ?? []} onChange={setPeriods} />
      <div className="mt-3 flex justify-end">
        <Button variant={ButtonVariant.PRIMARY} onClick={handleUse}>
          {label}
        </Button>
      </div>
    </div>
  );
}

/** On/off for a legacy flag that wasn't a recognized yes/no value. */
function ToggleEditor({ group, onResolve }: EditorProps): React.ReactElement {
  const handleOn = useCallback(() => onResolve(group, "1"), [group, onResolve]);
  const handleOff = useCallback(() => onResolve(group, "0"), [group, onResolve]);
  const scope = group.rows.length === 1 ? "" : ` (all ${group.rows.length})`;
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant={ButtonVariant.PRIMARY} onClick={handleOn}>
        Set to On{scope}
      </Button>
      <Button variant={ButtonVariant.GHOST} onClick={handleOff}>
        Set to Off{scope}
      </Button>
    </div>
  );
}

/** A replacement value: a timezone, a single phone number, or rewritten text. The server validates it. */
function TextEditor({ group, onResolve }: EditorProps): React.ReactElement {
  const [value, setValue] = useState("");
  const placeholder =
    group.item.inputKind === "timezone" ? "e.g. America/Chicago" : group.item.inputKind === "phone" ? "e.g. 555-123-4567" : "Value for Snowy";
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value), []);
  const handleUse = useCallback(() => onResolve(group, value.trim()), [group, value, onResolve]);
  return (
    <div className="flex flex-wrap items-end gap-2">
      <Input value={value} onChange={handleChange} placeholder={placeholder} containerClassName="min-w-64 flex-1" />
      <Button variant={ButtonVariant.PRIMARY} onClick={handleUse} disabled={value.trim() === ""}>
        {applyLabel(group)}
      </Button>
    </div>
  );
}
